import React from 'react';
import {
    Button,
    Col,
    FormGroup,
} from 'reactstrap';
import { isEmpty, isEqual } from 'lodash-es';
import cls from 'classnames';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import {
    faTruck, faTrashAlt, faPlus, faEdit, faEye, faTimes,
    faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { weekdayShort } from '@s20/i18n';
import { getTimeZoneAbbr } from '@s20/common/util/date';
import { getCounty } from '@s20/common/util/location';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm,
    FlexCenterRow,
    FlexColumnStart,
    FlexStartRow,
    FormDivider,
    FormGroupColumn,
    FormCheckbox,
    GroupedRow,
    onFieldChange,
    onReactSelectChanged,
    SmallOutlineButton,
    toasty,
    FormLabel,
    NumericInput,
    Input,
    Textarea,
} from '../common/forms/FormElements';
import CustomForms from '../customForm/CustomForms';
import { BaseFormViewModel } from '../common/ViewModel';
import FileUpload from '../common/forms/FileUpload';
import CommonContext, {
    ApiRoutes,
    AppNavPaths,
    ChargeTypeResourceTypes,
    Weekdays,
    ApplicationPermissions,
    SupportedFileExtensionString,
    CustomFormContext,
    ServiceResponseResult,
    resourceHasRequestedQuantity,
    resourceHasChargeTypeChoice,
} from '../Common';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import AddressFormNew from '../address/AddressFormNew';
import { handleFormSaveError } from '../common/forms/ValidationError';
import { Address } from '../address/Address';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import { OrganizationContact } from '../organization/Organization';
import { CountyLabel } from '../uscounties/CountyLabel';
import { ModalForm } from '../common/forms/ModalForm';
import { getCompanyContactsOptions } from '../contact/Contact';
import { ConfirmDelete } from '../common/Modal';
import { SaveButton } from '../common/forms/SaveButton';
import { requiredStyle } from '../projectManager/ResourcePanel';
import JobOverrideModal from './JobOverrideModal';
import { JobOverride } from './JobOverride';
import { Job } from './Job';
import { getShiftDifferentialWarning } from './common';
import { getDuplicateJobs, showDuplicateJobsModal } from './common/DuplicateJobDetection';


const getWeekValuesArrayOrdered = (jobWeek) => {
    // Safari sometimes reads this in the wrong format - it should always be stored as YYYY-MM-DD.
    // Formats are locale-independent: 'L' would change shape under non-English moment locales
    // (e.g. Spanish) and silently fail to parse the server's 'M/D/YYYY' values.
    const formats = ['YYYY-MM-DD', 'M/D/YYYY'];
    let startDate;

    formats.forEach((format) => {
        const date = moment(jobWeek, format);

        if (date.isValid()) {
            startDate = date.toDate();
        }
    });

    const dates = [];
    dates.push(startDate);
    for (let i = 1; i < 7; i += 1) {
        dates.push(
            moment(dates[i - 1])
                .add(1, 'day')
                .toDate(),
        );
    }
    return dates;
};

const getNonPlanningResourceName = (resourceName) => resourceName.startsWith('Flagger')
    ? resourceName.split(' ')[0]
    : null;

const notifyError = (message) => toasty.error(
    'Save Unsuccessful',
    <FlexColumnStart>
        <span>There was a server error when saving:</span>
        {!!message && (
            <span className="pt-2 pb-2 font-weight-bold">{`${message}`}</span>
        )}
        <span>
            Please try your request again or contact support for
            assistance.
        </span>
    </FlexColumnStart>,
);

const notifySuccess = () => toasty.success('Job Saved', 'Job saved successfully.');

const resourceHasAnyAssignments = (resource) => Boolean(
    resource
    && (resource.monday > 0
        || resource.tuesday > 0
        || resource.wednesday > 0
        || resource.thursday > 0
        || resource.friday > 0
        || resource.saturday > 0
        || resource.sunday > 0),
);

const resourceIsAfad = (resource) => Boolean(
    resource && resource.isAFAD,
);

const isSetupChargeRow = (resource) => resource?.jobResourceTypeId === ChargeTypeResourceTypes.Setup;

const doesAfadAssignmentExistForDay = (job, dayName) => {
    if (!job.resources) {
        return false;
    }

    return job.resources.some(
        (r) => resourceIsAfad(r) && r[dayName] && r[dayName] > 0,
    );
};

class JobForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.formRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.contactFormRef = React.createRef();
        this.customFormsRef = React.createRef();

        const stateBase = {
            job: new Job(),
            jobTypesOptions: [],
            dispatchLocations: [],
            weeks: [],
            usCounties: [],
            addressEditType: null, // from object const
            selectedAddressType: '',
            addressFormTitle: '',
            showTimesheetOverview: false,
            companyContactsOptions: [],
            projectManagerContactOptions: [],
            tableRequired: {},
            meetingLocationRequired: {},
            jobLocationRequired: {},
            pageModified: false,
            selectedContact: null,
            showShiftDifferentialWarning: false,
            customerSubcontractors: [],
            showContactsForm: false,
            showAddSetupCharge: false,
            showAddPermitFees: false,
            dispatchCompanies: [],
            currentDispatchCompany: null,
            // Property on the job to update with the newly created/edited contact.
            // job.requestedById or formenId.
            selectedContactIdProp: null,

            // Job override options
            showJobOverrideModal: false,
            currentJobOverride: null,
            // Add Upload Session ID
            uploadSessionId: `temp-${uuid()}`,

            edit: {
                dayStatuses: {},
                initialResources: null,
                allowRequestedBy: true,
                allowWeekOfDate: true,
                allowForemen: true,
                allowDispatchOfficeLocation: true,
                allowStartTime: true,
                allowWorkOrderNumber: true,
                // Needs special checks - what you can do is more nuanced.
                allowResources: true,
                allowMeetingLocation: true,
                allowJobLocation: true,
                // Currently will always be allowed.
                allowNotes: true,
                allowDelete: false,
                allowSubcontractor: true,
                allowProjectManagers: true,
                allowInspectorName: true,
                // Local tracking of resource errors.
                resourceErrors: {},
            },

            resourceTableError: '',
            contactFormIsReadOnly: false,
            allowContactTypeEdit: false,
            hasCustomForms: false,
            projectInformationInvalid: false,
            tenantSettings: {},
            dispatchPortLoading: false,
            dispatchPorts: [],
            duplicateJobs: [],
            ...new BaseFormViewModel(),
        };

        this.state = stateBase;
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onChangeResourceDay = this.onChangeResourceDay.bind(this);
        this.onChangeNonResourceDay = this.onChangeNonResourceDay.bind(this);
        this.onOrganizationContactSaved = this.onOrganizationContactSaved.bind(this);
        this.onDeleteConfirmed = this.onDeleteConfirmed.bind(this);
        this.onUpdateContacts = this.onUpdateContacts.bind(this);
        this.onDispatchOfficeChanged = this.onDispatchOfficeChanged.bind(this);
    }

    // #region METHODS
    componentDidMount() {
        return this.populateState();
    }

    setPageModified = (b) => this.setState({ pageModified: b });

    // #region CONTACTS
    onAddContact = (prop) => {
        const { job } = { ...this.state };
        this.context.setFormOpened(true);
        this.contactFormRef.current.resetForm();
        this.setState({
            selectedContact: {
                ...new OrganizationContact(),
                id: null /* new record */,
                companyId:
                    job.contract
                        .companyId /* this will be used to attach the new contact to the location. */,
            },
            selectedContactIdProp: prop,
            showContactsForm: true,
            allowContactTypeEdit: true,
            pageModified: true,
        });
    };

    onAddLocation = (type) => {
        const { job } = this.state;
        const selectedAddress = new Address(job.contract.countryId, job.contract.countryCode);

        const addressFormTitle = `New ${type === 'meetingLocation' ? 'Meeting' : 'Job'
            } Location`;
        this.setState({
            selectedAddressType: type,
            addressFormTitle,
        });

        this.addressFormRef.current.open(
            selectedAddress,
            addressFormTitle,
            true,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'
            }`,
        );
    };

    onAddPermitFees = () => {
        this.setState({ showAddPermitFees: false });
    };

    onAddPermitFeesClick = () => {
        this.setState({ showAddPermitFees: true });
    };

    onAddSetupCharge = () => {
        this.setState({ showAddSetupCharge: false });
    };

    onAddCertification = () => {
        this.setState({ showAddCertification: false });
    };

    onAddSetupChargeClick = () => {
        this.setState({ showAddSetupCharge: true });
    };

    onAddCertificationClick = () => {
        this.setState({ showAddCertification: true });
    };
    onChange = onFieldChange;

    onDispatchOfficeChanged(selection, meta = {}) {
        this.onSelectChanged(selection, meta);
        this.fetchDispatchPorts(selection?.value ?? null);
    }

    onChangeNonResourceDay = (nonResource, e) => {
        // prolly do something here
        const { job } = this.state;
        const updated = job.nonResources.map((r) => {
            if (r.id === nonResource.id) {
                return { ...r, [e.target.name]: e.target.checked };
            }
            return { ...r };
        });

        job.nonResources = updated;

        this.setState({ job, pageModified: true });
    };

    getShiftDifferentialWarning = () => {
        const { job, tenantSettings, dispatchLocations } = { ...this.state };

        const selectedDispatchLocation = this.getSelectedDispatchLocation() || {};
        const currentDispatchCompany = dispatchLocations.find((loc) => loc.value === selectedDispatchLocation.value);

        return getShiftDifferentialWarning({
            job, tenantSettings, currentDispatchCompany,
        });
    };

    onChangeResourceDay = (resource, e) => {
        const { job, edit } = { ...this.state };

        let val = parseInt(e.target.value);
        if (isNaN(val)) {
            val = 0;
        }
        if (val >= 1000) {
            toasty.error('Resource assignment cannot exceed 999.');
            return;
        }

        const dayName = e.target.name;
        const { resourceName } = resource;
        const msg = this.getResourceEditValidationMessage(dayName, resourceName, val);
        const nonPlanningResourceName = getNonPlanningResourceName(resourceName);
        const msgKey = `${nonPlanningResourceName}_${dayName}`;

        if (msg) {
            toasty.error('Resource Update', msg);
            // Flag an error on the resource for the day.
            edit.resourceErrors[msgKey] = msg;
        } else {
            delete edit.resourceErrors[msgKey];
        }

        // Update both the original day field and the generic field in the resource
        job.resources = job.resources.map((r) => {
            if (r.id === resource.id) {
                return {
                    ...r,
                    [dayName]: `${val}`,
                };
            }
            return { ...r };
        });

        // For planning-only resources, enforce zero if no AFAD assignment exists
        job.resources = job.resources.map((r) => {
            if (r.isPlanningOnly && !doesAfadAssignmentExistForDay(job, dayName)) {
                return { ...r, [dayName]: '0' };
            }
            return { ...r };
        });

        this.setState({ job, edit, pageModified: true });
        this.hydrateSetupCharges();
    };

    onChangeResourceChargeType = (resource, selection) => {
        const { job } = { ...this.state };

        job.resources = job.resources.map((r) => {
            if (r.id === resource.id) {
                return {
                    ...r,
                    chargeTypeId: selection?.value ?? null,
                };
            }
            return { ...r };
        });

        this.setState({ job, pageModified: true });
    };


    async onDeleteConfirmed() {
        const { job } = this.state;
        job.isDeleted = true;

        this.setState({
            originalJob: clone(job),
            pageModified: false,
            job,
        });

        await this.saveJob();
    }

    onEditContact = async (prop, readOnly = false) => {
        const { job } = { ...this.state };
        const selectedContact = await httpClient.js(
            ApiRoutes.locationContact.byId(job[prop]),
        );
        this.contactFormRef.current.resetForm();

        this.setState({
            selectedContact: { ...selectedContact },
            showContactsForm: true,
            selectedContactIdProp: prop,
            allowContactTypeEdit: false,
            pageModified: !readOnly,
            contactFormIsReadOnly: readOnly,
        });
    };

    onUpdateContacts = (contacts) => {
        this.setState({ companyContactsOptions: contacts });
    };

    onEditLocation = (type) => {
        const { job } = { ...this.state };
        const selectedAddress = { ...job[type] };
        const addressFormTitle = `Edit ${type === 'meetingLocation' ? 'Meeting' : 'Job'
            } Location`;

        this.setState({
            selectedAddress,
            selectedAddressType: type,
            addressFormTitle,
            pageModified: true,
        });

        this.addressFormRef.current.open(
            selectedAddress,
            addressFormTitle,
            true,
            `Also use as ${type === 'meetingLocation' ? 'Job Location' : 'Meeting Location'
            }`,
        );
    };

    onOrganizationContactSaved = async () => {
        this.setState({ contactFormIsReadOnly: true });

        const { selectedContact, selectedContactIdProp, job } = {
            ...this.state,
        };
        const isNew = parseInt(selectedContact.id ?? 0) <= 0;
        const result = await httpClient.andGetResponse(
            isNew ? httpClient.types.post : httpClient.types.put,
            isNew
                ? ApiRoutes.locationContact.create()
                : ApiRoutes.locationContact.update(selectedContact.id),
            { ...selectedContact },
            'Error Saving Contact',
        );

        if (result) {
            toasty.success('Contact saved.');

            // Make the contact the selected one - add returns just the Id, update returns object with id property.
            job[selectedContactIdProp] = result.id ?? result;

            // Refresh the contacts.
            const companyContacts = await getCompanyContactsOptions(
                job.contract.companyId,
            );

            // Update state.
            this.setState({
                companyContactsOptions: companyContacts.companyContactsOptions,
                projectManagerContactOptions: companyContacts.projectManagerContactOptions,
                job: { ...job },
                showContactsForm: false,
                saving: false,
            });
        } else {
            this.setState({ saving: false });
        }

        this.setState({ contactFormIsReadOnly: false });
    };
    // #endregion

    onPermitFeesApplyChange = () => {
        const { job } = this.state;

        job.permitFeesApply = !job.permitFeesApply;

        this.setState({ job });
    };

    onIsEmergencyChange = () => {
        const job = { ...this.state.job };

        job.isEmergency = !job.isEmergency;

        this.setState({ job });
    };

    onPreviewDrop = (files) => {
        this.setState((prevState) => ({
            files: prevState.files.concat(files),
        }));
    };

    onRemoveAttachment = async (file) => {
        const { job } = { ...this.state };
        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);

        // Instantly remove file from UI
        this.setState((prevState) => ({
            job: {
                ...prevState.job,
                uploads: prevState.job.uploads.filter((f) => f.name !== file.name),
            },
        }));

        try {
            const response = await httpClient.delete(url, file, httpClient.format.json);

            if (response) {
                toasty.success('Attachment removed.');
                // Fetch updated list to confirm the removal
                this.getAttachments();
            } else {
                const errorData = await response.json();
                toasty.error(errorData.message || 'Failed to remove the attachment. Try refreshing.');
            }
        } catch (error) {
            console.error(error);
            toasty.error('Could not delete attachment due to a server error.');
        }
    };

    onDownloadAttachment = async (file) => {
        const { job } = { ...this.state };
        if (!file) return;

        const url = ApiRoutes.job.attachment(job.id || this.state.uploadSessionId);
        await httpClient.downloadFile(url, file, file.name);
    };

    onSaveAddress = (address) => {
        let {
            job,
            selectedAddressType,
            meetingLocationRequired,
            jobLocationRequired,
        } = this.state;

        job[selectedAddressType] = { ...address };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation') {
                const mAddress = clone(address);
                mAddress.id = job.meetingLocation?.id ?? uuid();

                job.meetingLocation = mAddress;
            }

            if (selectedAddressType === 'meetingLocation') {
                const jAddress = clone(address);
                jAddress.id = job.jobLocation?.id ?? uuid();

                job.jobLocation = jAddress;
            }
        }

        if (job.meetingLocation) meetingLocationRequired = {};

        if (job.jobLocation) jobLocationRequired = {};

        this.setState({
            job,
            meetingLocationRequired,
            jobLocationRequired,
        });
    };

    onSelectChanged = onReactSelectChanged;

    fetchDispatchPorts = async (officeId) => {
        if (!officeId) {
            this.setState({ dispatchPorts: [], dispatchPortLoading: false });
            return;
        }
        this.setState({ dispatchPortLoading: true });
        const ports = await httpClient.js(ApiRoutes.typeAheads.dispatchPorts(officeId));
        const defaultPort = ports?.find((p) => p.isDefault) ?? null;
        this.setState((state) => {
            const portExists = ports?.some((p) => p.value === state.job.dispatchPortId);
            return {
                dispatchPorts: ports ?? [],
                dispatchPortLoading: false,
                job: portExists ? state.job : { ...state.job, dispatchPortId: defaultPort?.value ?? null },
            };
        });
    };

    onSubmit = async () => {
        const { job, newLocationContacts } = { ...this.state };

        if (!this.validateSetupCharges()) {
            return false;
        }

        if (!this.onValidate()) return false;

        if (!(this.customFormsRef.current?.validate() ?? true)) {
            this.setState({ saving: false });
            return false;
        }

        if (this.state.saving) return false;

        this.setState({ saving: true });
        // Revert uuid
        job.meetingLocation.id = typeof job.meetingLocation?.id === 'string'
            ? null
            : job.meetingLocation.id;
        job.jobLocation.id = typeof job.jobLocation?.id === 'string'
            ? null
            : job.jobLocation.id;
        job.resources = job.resources.map((r) => {
            r.id = typeof r.id === 'string' ? null : r.id;
            return r;
        });

        job.overrides = job.overrides?.map((o) => ({
            ...o,
            weekOfDate: job.weekOfDate,
            job: {
                weekOfDate: job.weekOfDate,
            },
        }));

        // Include new contacts
        job.newLocationContacts = newLocationContacts;

        // If job ID doesn't exist, pass TempSessionId
        if (!job.id) {
            job.tempSessionId = this.state.uploadSessionId; // Assign a new session if needed
        }
        if (job.startTimeOffset) {
            job.startTimeOffset = '';
        }

        this.setState({
            originalJob: clone(job),
            pageModified: false,
        });
        // Clear any fluent api errors
        this.clearErrors();
        await this.detectDuplicateJobs();
    };

    onValidate = () => {
        let {
            job,
            tableRequired,
            meetingLocationRequired,
            jobLocationRequired,
            edit,
            resourceTableError,
        } = this.state;

        let valid = true;

        tableRequired = {};
        meetingLocationRequired = {};
        jobLocationRequired = {};

        // The var sum below is unused?
        // Resources
        // at least 1 day on the entire grid needs to be 1 or more.  So sum entire grid and greater than 0
        resourceTableError = '';
        // let sum = 0;
        // (job.resources ?? []).forEach(x => {
        //    sum += parseInt(x.sunday) + parseInt(x.monday) + parseInt(x.tuesday) + parseInt(x.wednesday) + parseInt(x.thursday) + parseInt(x.friday) + parseInt(x.saturday);
        // });

        // Bypass this per CW
        // if (sum === 0) {
        //    valid = false;

        //    resourceTableError += "At least 1 resource is required for a job. ";

        //    tableRequired = requiredStyle;
        // }

        // Resources - added after "At least 1 resource" check.
        // Only applicable to job edits - not new jobs.
        const resourceErrorKeys = Object.keys(edit.resourceErrors);
        if (resourceErrorKeys.length > 0) {
            valid = false;

            resourceErrorKeys.forEach(
                (key) => (resourceTableError += `${edit.resourceErrors[key]} `),
            );

            tableRequired = requiredStyle;
        }

        // For non-project jobs, any day with assigned resources must have at least one employee resource.
        const dayKeys = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const resources = job.resources ?? [];
        for (const day of dayKeys) {
            const dayHasResources = resources.some((r) => !r.isPlanningOnly && (parseInt(r[day], 10) || 0) > 0);
            const dayHasEmployees = resources.some(
                (r) => r.jobResourceTypeId === ChargeTypeResourceTypes.Employee
                    && (parseInt(r[day], 10) || 0) > 0,
            );
            if (dayHasResources && !dayHasEmployees) {
                valid = false;
                const dayName = day.charAt(0).toUpperCase() + day.slice(1);
                resourceTableError += `${dayName} has resources assigned but no employee resource. `;
                tableRequired = requiredStyle;
            }
        }

        // Resources - equipment with multiple contract charge types must have one selected.
        resources
            .filter((r) => resourceHasChargeTypeChoice(r)
                && resourceHasRequestedQuantity(r)
                && !r.chargeTypeOptions.some((o) => o.chargeTypeId === r.chargeTypeId))
            .forEach((r) => {
                valid = false;
                resourceTableError += `Select a charge type for ${r.resourceName}. `;
                tableRequired = requiredStyle;
            });

        // Locations
        if (!job.meetingLocation) {
            valid = false;
            meetingLocationRequired = requiredStyle;
        }

        if (!job.jobLocation) {
            valid = false;
            jobLocationRequired = requiredStyle;
        }

        this.setState({
            formValidated: valid,
            saving: valid,
            tableRequired,
            meetingLocationRequired,
            jobLocationRequired,
            resourceTableError,
        });

        return valid;
    };

    getAttachments = async () => {
        const { job } = { ...this.state };
        const jobIdOrSessionId = job.id ? job.id : this.state.uploadSessionId;
        const attachments = await httpClient.get(
            ApiRoutes.job.getAllAttachments(jobIdOrSessionId),
        );
        job.uploads = attachments ?? [];
        this.setState((prevState) => ({
            job: {
                ...prevState.job,
                uploads: attachments ?? [],
            },
        }));
    };
    // #endregion

    getExistingOverrideDay = (day) => {
        const { job } = { ...this.state };

        const overrideDay = Weekdays[day.getDay()].name;
        const existingOverride = (job.overrides ?? []).find((override) => override.overrideDay === overrideDay && !override.isDeleted);

        return existingOverride;
    };

    /**
     * For cases where we have paired planning/resource versions of job resources
     * (as in the case of of "Flaggers" and "Flaggers (Planning)"), find the
     * paired version of the resource and extract its resource
     * count for the given day.
     * Planning resources are enabled by the EnableRequestedPersonnel tenant setting.
     * @param {*} resourceName
     * @param {*} day
     * @returns
     */
    getPairedResourceCount(resourceName, day) {
        const { job, tenantSettings } = this.state;

        if (!tenantSettings || !tenantSettings.enableRequestedPersonnel) {
            return null;
        }

        const nonPlanningResourceName = getNonPlanningResourceName(resourceName);

        if (nonPlanningResourceName == null) {
            return null;
        }

        const isNonPlanningResource = resourceName == nonPlanningResourceName;

        const alternateResource = job.resources.find(
            (r) => r.resourceName !== resourceName
                && r.resourceName.startsWith(nonPlanningResourceName),
        );

        if (
            !alternateResource
            // if we're editing Flaggers and Flaggers (Planning) is disabled because
            // an AFAD resource hasn't been assigned for the day, a zero value
            // on Flaggers (Planning) is not an issue
            || (isNonPlanningResource
                && !doesAfadAssignmentExistForDay(job, day))
        ) {
            return null;
        }

        return alternateResource[day];
    }



    getResourceEditValidationMessage(day, resourceName, val) {
        // You want a null message - means everything is OK.
        let msg = null;

        const resourceStatus = this.getResourceStatus(day, resourceName);

        if (resourceStatus && resourceStatus.statusCounts) {
            const maxStatus = resourceStatus.maxConfirmationStatusId;

            // If nothing has been assigned, you can edit whatever you'd like.
            if (maxStatus < 2) return null;

            // If the max status is "Scheduled", you cannot edit the value to go below assigned resources.
            // If the max status is 3 or 4, you can only add resources - number must be greater than the
            // counts of all statuses.
            const minCheckStatus = maxStatus === 2 ? 2 : 1;

            const threshold = Object.keys(resourceStatus.statusCounts).reduce(
                (acc, currentStatus) => {
                    // Set an upper limit of 4 in case we add future statuses.
                    if (currentStatus >= minCheckStatus && currentStatus <= 4) acc += resourceStatus.statusCounts[currentStatus];

                    return acc;
                },
                0,
            );

            if (val < threshold) {
                const du = `${day[0].toUpperCase()}${day.substring(1)}`;

                // A cancel-pending or threshold-cancelled assignment (5, 6) on the day doesn't free the
                // others' slots, so it is refused the same way as once notifications have gone out.
                msg = maxStatus === 2
                    ? `"${resourceName}" must be at least ${threshold} on ${du} because of existing assignments.`
                    : `"${resourceName}" must be at least ${threshold} on ${du} because notifications have already been sent.`;
            }
        }

        if (!msg) {
            const pairedResourceCount = this.getPairedResourceCount(
                resourceName,
                day,
            );

            if (pairedResourceCount !== null) {
                const uppercaseDay = `${day[0].toUpperCase()}${day.substring(
                    1,
                )}`;

                const isPlanning = resourceName.includes('Planning');

                if (isPlanning && Number(pairedResourceCount) > Number(val)) {
                    msg = `"${resourceName}" is fewer than the currently defined ${pairedResourceCount} resources on ${uppercaseDay}.`;
                } else if (
                    !isPlanning
                    && Number(pairedResourceCount) < Number(val)
                ) {
                    msg = `"${resourceName}" must be less than or equal to ${pairedResourceCount} on ${uppercaseDay} because of the configured planning count.`;
                }
            }
        }

        return msg;
    }

    toggleAllSetupCharges = (checked) => {
        const { job } = { ...this.state };

        job.resources = (job.resources ?? []).map((r) => {
            if (isSetupChargeRow(r) && r.chargeTypeId > 0 && !r.isPermit) {
                return { ...r, isNew: checked };
            }
            return r;
        });

        this.setState({ job }, () => {
            this.hydrateSetupCharges();
        });
    };

    getResourceStatus(day, resourceName) {
        let resourceStatus = null;

        const { edit } = this.state;

        if (
            edit.dayStatuses
            && edit.dayStatuses[day]
            && edit.dayStatuses[day].resourceStatuses
            && edit.dayStatuses[day].resourceStatuses[resourceName]
        ) {
            resourceStatus = edit.dayStatuses[day].resourceStatuses[resourceName];
        }

        return resourceStatus;
    }

    clearErrors = () => this.setState({ errors: {} });

    detectDuplicateJobs = async () => {
        this.setState({ duplicateJobs: [] });
        const { job } = this.state;
        const {
            weekOfDate,
            jobLocation,
            resources,
            overrides,
        } = job;
        const weekdays = getWeekValuesArrayOrdered(weekOfDate);

        const duplicateJobRequests = weekdays
            .map((date) => {
                // The resource's day columns are keyed by weekday name; the week itself starts on the job's own anchor day.
                const day = Weekdays[date.getDay()].name.toLowerCase();
                const hasResources = (resources ?? []).some((r) =>
                    !r.isPlanningOnly && Number(r[day] || 0) > 0,
                );

                if (!hasResources) return null;
                const override = (overrides ?? []).find(
                    (o) => !o.isDeleted
                        && o.jobLocation
                        && moment(o.overrideDate).isSame(moment(date), 'day'),
                );
                return {
                    jobDate: date.toISOString(),
                    jobLocation: override?.jobLocation ?? jobLocation,
                    excludeJobId: job.id ?? null,
                };
            })
            .filter(Boolean);

        const duplicateJobsList = await getDuplicateJobs({ duplicateJobRequests });
        if (duplicateJobsList.length > 0) {
            this.setState({ saving: false });
            const result = await showDuplicateJobsModal(duplicateJobsList);
            if (!result) return;
        }
        if (job.id) {
            await this.saveJob();
        } else {
            await this.createJob();
        }
    };
    createJob = async () => {
        const { job } = this.state;

        const response = await httpClient.post(
            ApiRoutes.job.create(),
            job,
            httpClient.format.none,
        );

        this.processSaveResponse(response, job.dispatchOfficeLocationId);
    };

    handleClickResourceDay = (day) => {
        const { job } = this.state;
        const existingOverride = this.getExistingOverrideDay(day);
        const override = existingOverride || {
            ...new JobOverride(day),
            dispatchPortId: job.dispatchPortId,
        };

        this.setState({
            showJobOverrideModal: true,
            currentJobOverride: {
                ...override,
                job,
            },
        });
    };

    handleCloseJobOverride = (data) => {
        if (data) {
            const { job } = { ...this.state };
            const overrides = (job.overrides ?? []);

            // Remove circular references
            data.job = {
                weekOfDate: data.job.weekOfDate,
            };

            const existingOverride = overrides.findIndex((override) => override.overrideDay === data.overrideDay);

            if (existingOverride === -1) {
                overrides.push(data);
            } else {
                overrides[existingOverride] = data;
            }

            this.setState({
                showJobOverrideModal: false,
                currentJobOverride: null,
                job: {
                    ...job,
                    overrides,
                },
            });
        } else {
            this.setState({
                showJobOverrideModal: false,
                currentJobOverride: null,
            });
        }
    };

    handleSaveError = handleFormSaveError;

    calcSetupQuantity = (row, resource, day) => {
        if (row && row[day] && row[day] > 0) {
            return 1;
        }

        const { tenantSettings } = { ...this.state };
        const quirksEnabled = tenantSettings?.quirksEnabled;

        if (!quirksEnabled) {
            return resource[day];
        }

        return 0;
    };

    hydrateSetupCharges = () => {
        const { job } = { ...this.state };
        if (!job?.resources?.length) {
            return;
        }

        const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        const anyAssignedRow = dayKeys.reduce((acc, dayKey) => {
            const anyAssigned = job.resources.some((res) =>
                !res.isPlanningOnly && Number(res[dayKey] || 0) > 0,
            );
            acc[dayKey] = anyAssigned ? 1 : 0;
            return acc;
        }, {});

        job.resources = job.resources.map((r) => {
            if (isSetupChargeRow(r) && r.chargeTypeId > 0 && (r.isNew === true || resourceHasAnyAssignments(r))) {
                r.monday = this.calcSetupQuantity(anyAssignedRow, r, 'monday');
                r.tuesday = this.calcSetupQuantity(anyAssignedRow, r, 'tuesday');
                r.wednesday = this.calcSetupQuantity(anyAssignedRow, r, 'wednesday');
                r.thursday = this.calcSetupQuantity(anyAssignedRow, r, 'thursday');
                r.friday = this.calcSetupQuantity(anyAssignedRow, r, 'friday');
                r.saturday = this.calcSetupQuantity(anyAssignedRow, r, 'saturday');
                r.sunday = this.calcSetupQuantity(anyAssignedRow, r, 'sunday');
            }

            return { ...r };
        });

        this.setState({ job });
    };

    isRequestedPersonnelSectionAvailable = () => {
        const { job } = this.state;

        return (
            job.resources
            && job.resources.some((r) => r.isPlanningOnly)
            && job.resources.some(
                (r) => resourceIsAfad(r) && resourceHasAnyAssignments(r),
            )
        );
    };

    overrideDayIsDisabled = (day) => {
        if (this.state.tenantSettings.disableJobOverrideTimeRestriction)
            return false;

        const now = moment();

        const selectedDay = typeof day === 'string' ? moment(day, moment.ISO_8601) : moment(day);

        // Restrict overrides to 7PM the previous day
        selectedDay.subtract(5, 'hours');

        return now.isAfter(selectedDay);
    };

    populateState = async () => {
        this.setState({ duplicateJobs: [] });
        let { showTimesheetOverview, edit } = { ...this.state };

        // Route params
        // Can route in from a contract page, or from job search, which can pull job by its ID
        // possibly add a way to select contract on here that job will assign to?
        const jobId = this.props.match.params.id; // {job/1}
        const { contractId } = this.props.match.params; // {newJob/1}


        // Promises
        const pDispatchLocs = httpClient.js(
            ApiRoutes.typeAheads.dispatchCompaniesForJob(jobId),
        );
        const pJobTypes = httpClient.js(ApiRoutes.typeAheads.jobTypes());
        const pJobManage = jobId
            ? httpClient.js(ApiRoutes.job.byId(jobId))
            : { job: new Job(), edit: {}, constrainContactTypes: true };

        const pContract = jobId
            ? null
            : httpClient.js(ApiRoutes.contract.byId(contractId)); // if new job, get the contract
        const pResources = jobId
            ? null
            : httpClient.js(ApiRoutes.job.getResources(contractId)); // if new job, get the resources
        const pWeeks = httpClient.js(ApiRoutes.job.weeks());

        let [
            dispatchLocations,
            jobManage,
            contract,
            resources,
            weeks,
            jobTypes,
            usCounties,
        ] = await Promise.all([
            pDispatchLocs,
            pJobManage,
            pContract,
            pResources,
            pWeeks,
            pJobTypes,
            httpClient.js(ApiRoutes.USCounties.all()),
        ]);

        const { job } = jobManage;
        if (!jobId) {
            job.contract = contract;

            // If it's a new job and there's only 1 dispatch location, set it.
            if (dispatchLocations.length === 1) {
                job.dispatchOfficeLocationId = dispatchLocations[0].value;
            }

            if (job.contract) {
                job.contractId = job.contract.id; // may need to gut this later
                job.description = job.contract.description;
                job.workOrderNumber = [contract.ecmsNumber, contract.pwProjectNumber]
                    .filter(Boolean)
                    .join(' - ');
            }
        } else {
            showTimesheetOverview = job.resources.filter((x) => x.typeName === 'None').length > 0;
        }

        const allowedDispatchLocationIds = job.contract?.dispatchLocations ?? [];
        if (allowedDispatchLocationIds.length > 0) {
            dispatchLocations = dispatchLocations.filter(
                (x) => allowedDispatchLocationIds.includes(x.value) || x.value === job.dispatchOfficeLocationId,
            );
        }

        if (resources) {
            job.resources = resources.map((r) => {
                if (r.id === 0) r.id = uuid();
                if (r.id === 0 && isSetupChargeRow(r) && r.chargeTypeId > 0 && !r.isPermit) r.isNew = true;
                return r;
            });
            showTimesheetOverview = resources.filter((x) => x.typeName === 'None').length > 0;
        }

        // Week selector setup
        if (!!(job.id ?? 0) > 0) {
            /** The job exists and we don't really support week start changes, so clear it out, set the existing job's week
                as the only week in the array, and lock it down. */
            weeks = [];
            weeks.push({ label: job.weekOfDate, value: job.weekOfDate });
        } else {
            // Get the calculated weeks from the tenant settings (same range as the dispatch board), and default to the current week
            const currentWeek = weeks.find((x) => x.current) ?? weeks[0];
            weeks = weeks.map((x) => ({ label: x.startDisplay, value: x.startDisplay }));
            job.weekOfDate = currentWeek.startDisplay;
        }

        // Copy updated edit permissions.
        edit = Object.assign(edit, jobManage.edit);
        if (job.resources) edit.initialResources = { ...job.resources };

        const customerId = job.contract.isIntercompany && !job.contract.doNotRequireEndCompany ? job.contract.endCompanyId : job.contract.companyId;

        const [companyContacts, customerSubcontractors, customFormTemplates, dispatchPorts] = await Promise.all([
            getCompanyContactsOptions(
                job.contract.companyId,
                jobManage.constrainContactTypes,
                job.requestedById,
                job.foremenId,
                job.projectManagerId,
            ),
            httpClient.js(ApiRoutes.typeAheads.subcontractors(customerId)),
            job.contract.companyId
                ? httpClient.post(ApiRoutes.customForm.forContext(), {
                    companyId: job.contract.companyId, formContextId: CustomFormContext.Job, contractId: job.contract?.id,
                })
                : Promise.resolve([]),
            job.dispatchOfficeLocationId
                ? httpClient.js(ApiRoutes.typeAheads.dispatchPorts(job.dispatchOfficeLocationId))
                : Promise.resolve([]),
        ]);

        const hasCustomForms = (customFormTemplates ?? []).length > 0;

        const originalJob = clone(job);

        const hasOnlyJobViewAccess = this.context.checkUserHasPermission(ApplicationPermissions.job_view) &&
            !this.context.checkUserHasPermission(ApplicationPermissions.job_create) &&
            !this.context.checkUserHasPermission(ApplicationPermissions.job_edit);

        const defaultPort = dispatchPorts?.find((p) => p.isDefault) ?? null;
        if (job.dispatchPortId != null && !dispatchPorts?.some((p) => p.value === job.dispatchPortId)) {
            job.dispatchPortId = defaultPort?.value ?? null;
        }

        this.setState({
            weeks: [...weeks],
            loading: false,
            dispatchLocations: [...dispatchLocations],
            mappedDispatchLocations: dispatchLocations.map((location) => ({ value: location.value, label: location.label, timeZone: location.timeZone })),
            customerSubcontractors: [...customerSubcontractors],
            job: { ...job },
            jobTypesOptions: [...jobTypes],
            edit: { ...edit },
            showTimesheetOverview,
            ...companyContacts,
            usCounties,
            originalJob,
            hasOnlyJobViewAccess,
            tenantSettings: this.context.tenant.tenantSettings,
            hasCustomForms,
            dispatchPorts: dispatchPorts ?? [],
        });
    };

    processSaveResponse = async (response, dispatchOfficeLocationId) => {
        const { user } = this.context;

        if (response.ok) {
            let responseData = null;
            try {
                responseData = await response.json();
            } catch (ex) {
                console.warn(ex);
            }

            const createdJobId = responseData?.data ?? null;
            this.resetForm();

            if (responseData?.result === ServiceResponseResult.OkWithErrors) {
                toasty.warning('Job Saved', responseData.message || 'Job saved, but employee notifications failed to send.');
            } else {
                notifySuccess();
            }

            let path = `${AppNavPaths.Dispatch}`;
            if (user.isAdmin || (user.expandedDispatchCompanies && user.expandedDispatchCompanies.length > 0 && user.expandedDispatchCompanies.includes(dispatchOfficeLocationId))) {
                path += `?dispatchOfficeId=${dispatchOfficeLocationId}`;
            }

            if (createdJobId) {
                const [pathname, searchString] = path.split('?');
                const params = new URLSearchParams(searchString ?? '');
                params.set('jobId', createdJobId);
                path = `${pathname}${params.toString() ? `?${params.toString()}` : ''}`;
            }

            this.context.history.push(path);
        } else {
            let message = '';
            try {
                const responseData = await response.json();
                message = responseData.message;
            } catch (ex) {
                console.warn(ex);
            } finally {
                notifyError(message);
                this.resetForm();
            }
        }
    };

    removeSetupCharge = (r) => {
        r.isNew = false;

        r.monday = 0;
        r.tuesday = 0;
        r.wednesday = 0;
        r.thursday = 0;
        r.friday = 0;
        r.saturday = 0;
        r.sunday = 0;

        this.hydrateSetupCharges();
    };

    removeCertifications = (resource) => {
        const { job } = { ...this.state };
        job.resources = job.resources.map((r) => {
            if (resource.certificationId === r.certificationId) {
                r.isNew = false;
                r.monday = 0;
                r.tuesday = 0;
                r.wednesday = 0;
                r.thursday = 0;
                r.friday = 0;
                r.saturday = 0;
                r.sunday = 0;
            }

            return { ...r };
        });

        this.setState({ job });
    };


    resetForm = () => this.setState((prevState) => ({ formValidated: false, saving: false, originalJob: clone(prevState.job) }));

    saveJob = async () => {
        const { job } = { ...this.state };

        const response = await httpClient.put(
            ApiRoutes.job.update(job.id),
            job,
            httpClient.format.none,
        );

        this.setPageModified(false);
        this.processSaveResponse(response, job.dispatchOfficeLocationId);
    };

    validateSetupCharges = () => {
        const { job } = this.state;

        if (job.contract.setupChargesRequired) {
            const setupCharges = job.resources.filter((r) => isSetupChargeRow(r) && r.chargeTypeId > 0);
            let hasSetupCharge = false;

            for (let i = 0; i < setupCharges.length; i += 1) {
                if (resourceHasAnyAssignments(setupCharges[i])) {
                    hasSetupCharge = true;
                    break;
                }
            }

            if (!hasSetupCharge) {
                toasty.error('At least one setup charge is required.');
                return false;
            }
        }

        return true;
    };

    renderResourceHeader = (weekdays, isPlanningOnly) => {
        const { job } = { ...this.state };
        return (
            <thead>
                <tr>
                    <th>Resource</th>
                    <th>Type</th>

                    {weekdays.map((day) => {
                        if (!day) {
                            return null;
                        }
                        const dayObj = Weekdays[day.getDay()];
                        const dispatchCancelled = job.dayCancelled?.some((d) => d === dayObj.id);
                        const existing = this.getExistingOverrideDay(day);
                        const overrideDisabled = this.overrideDayIsDisabled(day);

                        if (dispatchCancelled) {
                            return (
                                <th
                                    key={day}
                                    className="resource-weekday-col text-center"
                                >
                                    <div className="d-flex flex-column">
                                        <span>
                                            {weekdayShort(day.getDay())}
                                        </span>

                                        <span>{moment(day).format('M/D')}</span>
                                        <span>Cancelled</span>
                                    </div>
                                </th>
                            );
                        }
                        if (isPlanningOnly || (overrideDisabled && !existing) || !this.state.tenantSettings.enableJobOverrides) {
                            return (
                                <th
                                    key={day}
                                    className="resource-weekday-col text-center"
                                >
                                    <div className="d-flex flex-column">
                                        <span>
                                            {weekdayShort(day.getDay())}
                                        </span>

                                        <span>{moment(day).format('M/D')}</span>
                                    </div>
                                </th>
                            );
                        }

                        return (
                            <th
                                key={day}
                                className="resource-weekday-col text-center"
                            >
                                <Button
                                    color="link"
                                    size="sm"
                                    className={`d-flex align-items-center flex-column site-link ${existing && 'text-info'}`}
                                    role="button"
                                    onClick={() => this.handleClickResourceDay(day)}
                                >
                                    <span>
                                        {weekdayShort(day.getDay())}
                                    </span>

                                    <span>{moment(day).format('M/D')}</span>

                                    <FontAwesomeIcon
                                        icon={overrideDisabled ? faEye : faEdit}
                                        size="sm"
                                        className="ml-1"
                                    />
                                </Button>
                            </th>
                        );
                    })}
                    <th />
                </tr>
            </thead>
        );
    };

    renderResources = (weekdays, isPlanningOnly) => {
        const { job, edit, tenantSettings } = { ...this.state };

        if (!job.resources || !(weekdays ?? []).length) {
            return '';
        }
        return job.resources
            .filter((r) =>
                r.isPlanningOnly == Boolean(isPlanningOnly) &&
                (
                    (!isSetupChargeRow(r) && r.certificationId == null)
                    || (isSetupChargeRow(r)
                        && (r.isNew === true || resourceHasAnyAssignments(r)))
                    || (r.certificationId > 0
                        && (r.isNew === true || resourceHasAnyAssignments(r)))
                ),
            )
            .map((resource) => {
                const chargeTypeOptions = (resource.chargeTypeOptions ?? []).map((o) => ({
                    value: o.chargeTypeId,
                    label: o.description,
                }));
                const anyDayHasTimesheet = Weekdays.some(
                    (d) => resource[`${d.name.toLowerCase()}HasTimesheet`] === true,
                );
                const showChargeTypeSelect = !isPlanningOnly && resourceHasChargeTypeChoice(resource);

                return (
                    <tr key={resource.id}>
                        <td style={showChargeTypeSelect ? { minWidth: '200px' } : undefined}>
                            {showChargeTypeSelect ? (
                                <ValidatedSelect
                                    id={`resourceChargeType_${resource.id}`}
                                    name={`resourceChargeType_${resource.id}`}
                                    placeholder={resource.resourceName}
                                    options={chargeTypeOptions}
                                    value={chargeTypeOptions.find((o) => o.value === resource.chargeTypeId) ?? ''}
                                    onChange={this.onChangeResourceChargeType.bind(this, resource)}
                                    required={resourceHasRequestedQuantity(resource)}
                                    error={`A charge type selection is required for ${resource.resourceName}.`}
                                    isDisabled={chargeTypeOptions.some((o) => o.value === resource.chargeTypeId)
                                        && (anyDayHasTimesheet || !edit.allowResources)}
                                    hideClear
                                />
                            ) : resource.resourceName}
                        </td>
                        <td>{resource.typeName}</td>
                        {weekdays.map((day) => {
                            const dayObj = Weekdays[day.getDay()];
                            const dayName = dayObj.name.toLowerCase();
                            const dayNameHasTimesheet = `${dayName}HasTimesheet`;
                            const dispatchCancelled = job.dayCancelled?.some((d) => d === dayObj.id);

                            const finalValue = resource[dayName];
                            const resourceIsDisabled = resource[dayNameHasTimesheet] === true || dispatchCancelled ||
                                (isPlanningOnly && !doesAfadAssignmentExistForDay(job, dayName));

                            let title = '';
                            if (dispatchCancelled) {
                                title = 'Job Cancelled';
                            } else if (resource[dayNameHasTimesheet] === true) {
                                title = 'Approved Timesheet Exists';
                            }

                            return (
                                <td
                                    key={day}
                                    className="resource-weekday-column"
                                >
                                    <NumericInput
                                        className="form-control-sm"
                                        style={{ minWidth: '55px' }}
                                        id={`r${dayName}`}
                                        name={`${dayName}`}
                                        required
                                        value={finalValue}
                                        step={1}
                                        min={0}
                                        wholeNumber
                                        onChange={this.onChangeResourceDay.bind(
                                            this,
                                            resource,
                                        )}
                                        readOnly={!edit.allowResources
                                            || (isSetupChargeRow(resource) && resource.chargeTypeId > 0 && !!tenantSettings.quirksEnabled)}
                                        disabled={resourceIsDisabled}
                                        title={title}
                                    />
                                </td>
                            );
                        })}
                        <td
                            style={{
                                textAlign: 'center', paddingLeft: '0px', paddingRight: '0px', paddingTop: '7px',
                            }}
                        >
                            {(isSetupChargeRow(resource) || resource.certificationId > 0) && (
                                <FontAwesomeIcon
                                    icon={faTimes}
                                    title={
                                        isSetupChargeRow(resource)
                                            ? 'Remove setup charge'
                                            : 'Remove certification'
                                    }
                                    className="text-danger cursor-pointer"
                                    onClick={() =>
                                        isSetupChargeRow(resource)
                                            ? this.removeSetupCharge(resource)
                                            : this.removeCertifications(resource)
                                    }
                                />
                            )}

                        </td>
                    </tr>
                );
            });
    };

    getTzAbbreviation = () => {
        const { job } = this.state;
        if (!job) return '';

        return getTimeZoneAbbr(job.startTimeOffset, job.jobLocation?.timeZone, job.weekOfDate, job.startTime);
    };

    getSelectedDispatchLocation = () => {
        const { mappedDispatchLocations, job } = this.state;

        return (mappedDispatchLocations ?? []).find(
            (x) => x.value === job.dispatchOfficeLocationId,
        );
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete Job',
            onSubmit: () => this.onDeleteConfirmed(),
            body: () => 'Are you sure you want to delete this job?',
        });
    };

    render() {
        const {
            loading,
            job,
            errors,
            formValidated,
            mappedDispatchLocations,
            saving,
            weeks,
            companyContactsOptions,
            tableRequired,
            meetingLocationRequired,
            jobLocationRequired,
            selectedContact,
            showContactsForm,
            allowContactTypeEdit,
            edit,
            resourceTableError,
            customerSubcontractors,
            contactFormIsReadOnly,
            showAddSetupCharge,
            showAddCertification,
            showJobOverrideModal,
            currentJobOverride,
            showAddPermitFees,
            pageModified,
            originalJob,
            projectManagerContactOptions,
            jobTypesOptions,
            hasOnlyJobViewAccess,
            hasCustomForms,
            dispatchPortLoading,
            dispatchPorts,
        } = this.state;

        const requestedPersonnelSectionAvailable = this.isRequestedPersonnelSectionAvailable();

        const showConfirmationPrompt = (pageModified || !isEqual(job, originalJob));
        const existing = !!job.id;

        const unusedSetupChargeTypes = job.resources.filter((r) => isSetupChargeRow(r) && r.chargeTypeId > 0 && !r.isPermit);
        const hasSetupCharges = (unusedSetupChargeTypes ?? []).length > 0;
        const allSetupChargesSelected = hasSetupCharges && (unusedSetupChargeTypes ?? []).every((c) => c.isNew === true);
        const selectedCount = (unusedSetupChargeTypes ?? []).filter((c) => c.isNew === true).length;
        const selectAllCta = allSetupChargesSelected ? 'Clear all' : 'Select all';

        const unusedCertifications = job.contract.certificationRequired
            ? job.resources.filter((r) => r.certificationId > 0)
            : [];
        const unusedPermitFees = job.resources.filter((r) => isSetupChargeRow(r) && r.chargeTypeId > 0 && r.isPermit);

        const { jobUploadsEnabled } = {
            ...this.context.tenant.tenantSettings,
        };

        const weekdays = getWeekValuesArrayOrdered(job.weekOfDate);
        const selectedDispatchLocation = this.getSelectedDispatchLocation();

        const countyWithMeetingLocation = getCounty(this.state.usCounties, {
            usCountyId: job.meetingLocation?.usCountyId,
            contractUnionWorkTypeId: job.contract.contractUnionWorkTypeId,
        });

        const countyWithJobLocation = getCounty(this.state.usCounties, {
            usCountyId: job.jobLocation?.usCountyId,
            contractUnionWorkTypeId: job.contract.contractUnionWorkTypeId,
        });

        return (
            <>
                <FormPrompt
                    when={!!showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="jobForm"
                    formHeadingIcon={faTruck}
                    formHeading={
                        !existing
                            ? 'New Job'
                            : `Edit Job: ${(job.contract ?? {}).number}${job.contractSequence
                                ? `-${job.contractSequence}`
                                : ''
                            }`
                    }
                    formName="jobForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    isValid={this.isFormValid}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={formValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    backUrl={job.contract ? `${AppNavPaths.Contract}/${job.contract?.id}` : `${AppNavPaths.Job}`}
                    backLabel={job.contract ? 'Back to Contract' : 'Back to Jobs'}
                    viewOnly={hasOnlyJobViewAccess}
                >
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Contract Number"
                                id="contractNumber"
                                name="contractNumber"
                                readOnly
                                className="form-control-plaintext no-readonly-bg "
                                value={(job.contract ?? {}).number}
                            />
                            <Input
                                label="Description"
                                id="contractDescription"
                                name="contractDescription"
                                readOnly
                                className="form-control-plaintext no-readonly-bg"
                                value={(job.contract ?? {}).description}
                            />
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <Input
                                label="Customer Name"
                                id="customerName"
                                name="customerName"
                                readOnly
                                className="form-control-plaintext no-readonly-bg"
                                value={(job.contract ?? {}).customerName}
                            />
                            {(job.contract ?? {}).operationsCenterName && (
                                <Input
                                    label="Operations Center"
                                    id="operationsCenterName"
                                    name="operationsCenterName"
                                    readOnly
                                    className="form-control-plaintext no-readonly-bg"
                                    value={
                                        (job.contract ?? {})
                                            .operationsCenterName
                                    }
                                />
                            )}

                            {(job.contract?.isUnionWork === true || job.contract?.companyUsesContractSchedule === true) && (
                                <Input
                                    label="Schedule"
                                    id="schedule"
                                    name="schedule"
                                    readOnly
                                    className="form-control-plaintext no-readonly-bg"
                                    value={
                                        (job.contract ?? {})
                                            .contractScheduleTypeName
                                    }
                                />
                            )}
                        </FormGroupColumn>
                    </GroupedRow>

                    <GroupedRow>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="weekOfDate"
                                name="job.weekOfDate"
                                label="Week Of"
                                required
                                hideClear
                                options={weeks}
                                value={
                                    weeks.find(
                                        (x) => x.value === job.weekOfDate,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                error="A week start selection is required."
                                isDisabled={!edit.allowWeekOfDate}
                            />
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="requestedBy"
                                name="job.requestedById"
                                label="Requested By"
                                options={companyContactsOptions}
                                value={
                                    (companyContactsOptions ?? []).find(
                                        (x) => x.value
                                            === job.requestedById,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                required
                                error="A requested by selection is required."
                                isDisabled={!edit.allowRequestedBy}
                                showAddButton={!job?.hasAssignments}
                                onAddButtonClick={() => this.onAddContact(
                                    'requestedById',
                                )}
                                showEditButton={!!job?.requestedById}
                                onEditButtonClick={() => this.onEditContact(
                                    'requestedById',
                                )}
                                showViewButton={!!job?.requestedById}
                                onViewButtonClick={() => this.onEditContact(
                                    'requestedById', true,
                                )}
                            />
                        </FormGroupColumn>
                    </GroupedRow>
                    <GroupedRow>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="dispatchOfficeLocation"
                                label="Dispatching"
                                name="job.dispatchOfficeLocationId"
                                options={mappedDispatchLocations}
                                value={selectedDispatchLocation ?? ''}
                                onChange={this.onDispatchOfficeChanged}
                                required
                                error="A dispatch selection is required."
                                isDisabled={!edit.allowJobLocation}
                            />
                            {!!job.dispatchOfficeLocationId && !!dispatchPorts.length && (
                                <ValidatedSelect
                                    id="dispatchPortId"
                                    name="job.dispatchPortId"
                                    options={dispatchPorts}
                                    label="Dispatch Port"
                                    placeholder="Use office location"
                                    value={dispatchPorts.find((o) => o.value === job.dispatchPortId) ?? null}
                                    onChange={this.onSelectChanged}
                                    isLoading={dispatchPortLoading}
                                    disabled={dispatchPortLoading}
                                />
                            )}
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <ValidatedSelect
                                id="foremen"
                                name="job.foremenId"
                                label="Foreman"
                                options={companyContactsOptions}
                                value={
                                    (companyContactsOptions ?? []).find(
                                        (x) => x.value === job.foremenId,
                                    ) ?? ''
                                }
                                onChange={this.onSelectChanged}
                                required
                                error="A foreman selection is required."
                                isDisabled={!edit.allowForemen}
                                showAddButton={!job?.hasAssignments}
                                onAddButtonClick={() => this.onAddContact(
                                    'foremenId',
                                )}
                                showEditButton={!!job?.foremenId}
                                onEditButtonClick={() => this.onEditContact(
                                    'foremenId', !edit.allowForemen,
                                )}
                                showViewButton={!!job?.foremenId}
                                onViewButtonClick={() => this.onEditContact(
                                    'foremenId', true,
                                )}
                            />
                        </FormGroupColumn>
                    </GroupedRow>
                    <GroupedRow>
                        {(job.contract ?? {}).invoiceHasInspectorName && (
                            <FormGroupColumn>
                                <Input
                                    id="inspectorName"
                                    name="job.inspectorName"
                                    label="Inspector Name"
                                    value={
                                        job.inspectorName ?? ''
                                    }
                                    onChange={this.onChange}
                                    readOnly={
                                        !edit.allowInspectorName
                                    }
                                />
                            </FormGroupColumn>
                        )}
                        {(job.contract ?? {}).subcontractorsApply && (
                            <FormGroupColumn>
                                <ValidatedSelect
                                    id="subcontractor"
                                    name="job.subcontractorCompanyId"
                                    label="Subcontractor"
                                    options={customerSubcontractors}
                                    value={
                                        (
                                            customerSubcontractors ?? []
                                        ).find(
                                            (x) => x.value
                                                === job.subcontractorCompanyId,
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    required
                                    error="A subcontractor selection is required."
                                />
                            </FormGroupColumn>
                        )}
                    </GroupedRow>

                    {(job.contract ?? {}).requireProjectInformation && (
                        <GroupedRow>
                            <FormGroupColumn>
                                <Input
                                    label="Project Name"
                                    id="projectName"
                                    name="job.projectName"
                                    value={job.projectName ?? ''}
                                    onChange={this.onChange}
                                    error="Project Name is required."
                                    required
                                />
                            </FormGroupColumn>
                            <FormGroupColumn>
                                <FormGroup className="relative">
                                    <ValidatedSelect
                                        id="projectManager"
                                        name="job.projectManagerId"
                                        label="Project Manager"
                                        options={projectManagerContactOptions}
                                        value={
                                            (companyContactsOptions ?? []).find(
                                                (x) => x.value === job.projectManagerId,
                                            ) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                        required
                                        error="A Project Manager selection is required."
                                        isDisabled={!edit.allowProjectManagers}
                                        showAddButton
                                        onAddButtonClick={() => this.onAddContact(
                                            'projectManagerId',
                                        )}
                                        showEditButton={!!job?.projectManagerId}
                                        onEditButtonClick={() => this.onEditContact(
                                            'projectManagerId',
                                        )}
                                        showViewButton={!!job?.projectManagerId}
                                        onViewButtonClick={() => this.onEditContact(
                                            'projectManagerId', true,
                                        )}
                                    />
                                </FormGroup>
                            </FormGroupColumn>
                        </GroupedRow>
                    )}
                    <GroupedRow>
                        <FormGroupColumn>
                            <FormGroup className="mb-1">
                                <FormCheckbox
                                    id="isEmergency"
                                    name="isEmergency"
                                    checked={job.isEmergency ?? false}
                                    onChange={this.onIsEmergencyChange}
                                    labelText="This is an emergency job"
                                />
                            </FormGroup>
                        </FormGroupColumn>
                    </GroupedRow>
                    <FormDivider />
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                label="Start Time"
                                id="startTime"
                                required
                                type="time"
                                name="job.startTime"
                                value={job.startTime ?? ''}
                                onChange={this.onChange}
                                readOnly={!edit.allowStartTime}
                                error="Start Time is required."
                                helperText={this.getShiftDifferentialWarning()}
                                endAddonClassName="inset"
                                endAddon={(
                                    <span className="text-muted">
                                        {this.getTzAbbreviation()}
                                    </span>
                                )}

                            />

                            {job?.jobTypeId !== 1 && (
                                <ValidatedSelect
                                    id="jobType"
                                    name="job.jobTypeId"
                                    label="What type of work is being done?"
                                    options={jobTypesOptions}
                                    value={(jobTypesOptions ?? []).find((x) => x.value === job.jobTypeId) ?? ''}
                                    onChange={this.onSelectChanged}
                                    required={(jobTypesOptions ?? []).some((x) => x.timesheetRequiresWorkflowNotes)}
                                    error="A job type must be selected"
                                />
                            )}

                            {hasCustomForms && (
                                <CustomForms
                                    ref={this.customFormsRef}
                                    label="Job Forms"
                                    className="mb-2"
                                    isEmergency={job.isEmergency}
                                    tooltipId="forms-incomplete-icon"
                                    disabled={!job.contract?.companyId}
                                    jobId={job.id || null}
                                    companyId={job.contract?.companyId}
                                    contractId={job.contract?.id}
                                    controlValues={{ requiresJobApproval: !job.isEmergency }}
                                    readOnly={hasOnlyJobViewAccess}
                                    sessionId={this.state.uploadSessionId}
                                    formContextId={CustomFormContext.Job}
                                    validationMessage="Complete and Save Job Forms."
                                />
                            )}
                        </FormGroupColumn>

                        <FormGroupColumn>
                            {job.contract.workOrderTypeId !== 3 && (
                                <Input
                                    label="Work Order"
                                    id="workOrderNumber"
                                    name="job.workOrderNumber"
                                    value={job.workOrderNumber ?? ''}
                                    onChange={this.onChange}
                                    readOnly={!edit.allowWorkOrderNumber}
                                    maxLength={44}
                                />
                            )}

                            {job.contract.companyRequiresWorkRequest && (
                                <Input
                                    label="Work Request Number"
                                    id="workRequest"
                                    name="job.workRequest"
                                    value={job.workRequest ?? ''}
                                    onChange={this.onChange}
                                    maxLength={44}
                                />
                            )}
                            {job.contract.companyRequiresPurchaseOrderNumber && (
                                <Input
                                    label="PO Number"
                                    id="purchaseOrderNumber"
                                    name="job.purchaseOrderNumber"
                                    value={job.purchaseOrderNumber ?? ''}
                                    onChange={this.onChange}
                                    maxLength={44}
                                />
                            )}
                        </FormGroupColumn>
                    </GroupedRow>
                    <GroupedRow>
                        <Col>
                            <FormGroup className="mb-3">
                                <FormLabel
                                    label="Resources"
                                    required
                                />
                                <div style={tableRequired}>
                                    <table
                                        className={cls(
                                            'table table-sm table-bordered',
                                            {
                                                'border-top-0':
                                                    this.props.noTopBorder,
                                            },
                                        )}
                                    >
                                        {this.renderResourceHeader(
                                            weekdays,
                                        )}
                                        <tbody>
                                            {!(job.resources ?? []).length ?
                                                (
                                                    <tr key="emptyRow">
                                                        <td
                                                            colSpan="9"
                                                            className="text-center"
                                                        >
                                                            <small>
                                                                No resources
                                                                found for this
                                                                job.
                                                            </small>
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    this.renderResources(
                                                        weekdays,
                                                    )
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                                {!isEmpty(tableRequired) && (
                                    <small className="text-danger">
                                        {resourceTableError}
                                    </small>
                                )}
                            </FormGroup>
                        </Col>
                    </GroupedRow>

                    {requestedPersonnelSectionAvailable && (
                        <GroupedRow>
                            <Col>
                                <FormGroup className="mb-0">
                                    <FormLabel
                                        label="Requested Personnel"
                                        required
                                    />
                                    <div style={tableRequired}>
                                        <table
                                            className={cls(
                                                'table table-sm table-bordered',
                                                {
                                                    'border-top-0':
                                                        this.props
                                                            .noTopBorder,
                                                },
                                            )}
                                        >
                                            {this.renderResourceHeader(
                                                weekdays,
                                                true,
                                            )}
                                            <tbody>
                                                {this.renderResources(
                                                    weekdays,
                                                    true,
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </FormGroup>
                            </Col>
                        </GroupedRow>
                    )}
                    <div className="d-flex gap-2">
                        {job.contract.setupChargesRequired
                            && (
                                <GroupedRow>
                                    <FormGroup>
                                        <div style={{ marginLeft: '30px' }}>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                name="jobForm"
                                                onClick={this.onAddSetupChargeClick}
                                            >
                                                <FontAwesomeIcon
                                                    className="mr-2"
                                                    icon={faPlus}
                                                />
                                                Add Setup Charge
                                            </Button>
                                        </div>
                                    </FormGroup>
                                </GroupedRow>
                            )}


                        {job.contract.certificationRequired && (
                            <GroupedRow>
                                <FormGroup>
                                    <div style={{ marginLeft: '30px' }}>
                                        <Button
                                            size="sm"
                                            color="primary"
                                            name="jobForm"
                                            onClick={this.onAddCertificationClick}
                                        >
                                            <FontAwesomeIcon
                                                className="mr-2"
                                                icon={faPlus}
                                            />
                                            Add Certification Requirement
                                        </Button>
                                    </div>
                                </FormGroup>
                            </GroupedRow>
                        )}

                    </div>
                    {job.contract.permitFeesApply
                        && (
                            <FormGroup style={{ marginBottom: '5px' }}>
                                <div style={{ marginLeft: '30px' }}>
                                    <FormCheckbox
                                        id="permitFeesApply"
                                        name="permitFeesApply"
                                        checked={
                                            job.permitFeesApply ?? ''
                                        }
                                        onChange={this.onPermitFeesApplyChange}
                                        labelText="Permit Fees Apply"
                                    />
                                </div>
                            </FormGroup>
                        )}

                    {job.contract.permitFeesApply && job.permitFeesApply
                        && (
                            <FormGroup>
                                <div style={{ marginLeft: '60px' }}>
                                    <Button
                                        size="sm"
                                        color="secondary"
                                        name="jobForm"
                                        onClick={this.onAddPermitFeesClick}
                                    >
                                        <FontAwesomeIcon
                                            className="mr-2"
                                            icon={faPlus}
                                        />
                                        Add Permit Fees...
                                    </Button>
                                </div>
                            </FormGroup>
                        )}

                    <GroupedRow>
                        <FormGroupColumn>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="meetingLocation"
                                    label="Meeting Location"
                                    required
                                />
                                {job.meetingLocation ? (
                                    <FlexStartRow>
                                        <SmallOutlineButton
                                            onClick={() => {
                                                this.onEditLocation(
                                                    'meetingLocation',
                                                );
                                            }}
                                            label="Edit"
                                            disabled={
                                                !edit.allowMeetingLocation
                                            }
                                        />

                                        <span className="form-control-plaintext ml-2">
                                            {job.meetingLocation.address1}
                                            {' '}

                                            {job.meetingLocation.city}
                                            {', '}

                                            {job.meetingLocation.state}
                                            {', '}

                                            {job.meetingLocation.zip}
                                            {' '}

                                            (
                                            <CountyLabel {...countyWithMeetingLocation} />
                                            )
                                        </span>
                                    </FlexStartRow>
                                ) : (
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="sm"
                                        style={meetingLocationRequired}
                                        onClick={() => this.onAddLocation(
                                            'meetingLocation',
                                        )}
                                    >
                                        Add Meeting Location
                                    </Button>
                                )}
                                {!isEmpty(meetingLocationRequired) && (
                                    <small className="text-danger">
                                        Meeting location is required.
                                    </small>
                                )}
                            </FormGroup>
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="jobLocation"
                                    label="Job Location"
                                    required
                                />

                                {job.jobLocation ? (
                                    <FlexStartRow>
                                        <SmallOutlineButton
                                            onClick={() => {
                                                this.onEditLocation(
                                                    'jobLocation',
                                                );
                                            }}
                                            label="Edit"
                                            disabled={
                                                !edit.allowJobLocation
                                            }
                                        />

                                        <span className="form-control-plaintext ml-2">
                                            {job.jobLocation.address1}
                                            {' '}

                                            {job.jobLocation.city}
                                            {', '}

                                            {job.jobLocation.state}
                                            {', '}

                                            {job.jobLocation.zip}
                                            {' '}

                                            (
                                            <CountyLabel {...countyWithJobLocation} />
                                            )
                                        </span>
                                    </FlexStartRow>
                                ) : (
                                    <Button
                                        type="button"
                                        color="secondary"
                                        size="sm"
                                        style={jobLocationRequired}
                                        onClick={() => {
                                            this.onAddLocation(
                                                'jobLocation',
                                            );
                                        }}
                                    >
                                        Add Job Location
                                    </Button>
                                )}
                                {!isEmpty(jobLocationRequired) && (
                                    <small className="text-danger">
                                        Job location is required.
                                    </small>
                                )}
                            </FormGroup>
                        </FormGroupColumn>
                    </GroupedRow>
                    <GroupedRow>
                        <Col>
                            <Textarea
                                label="Notes"
                                id="notes"
                                name="job.notes"
                                value={job.notes ?? ''}
                                onChange={this.onChange}
                                placeholder="Enter notes to field staff here."
                                maxLength="1000"
                                rows="5"
                                readOnly={!edit.allowNotes}
                            />
                        </Col>
                    </GroupedRow>
                    {!!jobUploadsEnabled && (
                        <GroupedRow>
                            <Col>
                                <FormGroup>
                                    <FormLabel
                                        text={(
                                            <>
                                                Attach Files
                                                <small className="text-muted d-block mt-1">
                                                    Max 40MB | Supported: {SupportedFileExtensionString}
                                                </small>
                                            </>
                                        )}
                                    />
                                    <FileUpload
                                        id="jobUploader"
                                        accept={SupportedFileExtensionString}
                                        maxNameLength={150}
                                        url={ApiRoutes.job.postAttachments(
                                            job.id || this.state.uploadSessionId,
                                        )}
                                        onUploadComplete={() => this.getAttachments()}
                                        files={job.uploads || []}
                                        onDownload={this.onDownloadAttachment}
                                        onDelete={this.onRemoveAttachment}
                                    />
                                </FormGroup>
                            </Col>
                        </GroupedRow>
                    )}
                    <FlexCenterRow>
                        <SaveButton
                            saving={saving}
                            disabled={dispatchPortLoading}
                            label={!existing
                                ? 'Save New Job'
                                : 'Save Job Details'}
                        />
                    </FlexCenterRow>
                    <FlexCenterRow className="mb-3 mt-4">
                        {edit.allowDelete && (
                            <Button
                                size="sm"
                                type="button"
                                color="danger"
                                name="jobForm"
                                onClick={() => this.showDeleteModal()}
                            >
                                <FontAwesomeIcon
                                    className="mr-1"
                                    icon={faTrashAlt}
                                />
                                Delete Job
                            </Button>
                        )}
                    </FlexCenterRow>
                </AppPageForm>

                {!loading && (
                    <>
                        <AddressFormNew
                            id="jobAddressForm"
                            ref={this.addressFormRef}
                            contractUsesPrevailingWage={job.contract.prevailingWage || job.contract.requirePaySchedule}
                            isUnionWork={job.contract.isUnionWork}
                            contractId={job.contractId}
                            workTypeId={job.contract?.contractUnionWorkTypeId}
                            countryId={job.contract.countryId}
                            countryCode={job.contract.countryCode}
                            onSaveCallback={(address) => this.onSaveAddress(address)}
                            usCounties={this.state.usCounties}
                            timeZone={selectedDispatchLocation?.timeZone}
                            namePrefix=""
                        />

                        <OrganizationContactForm
                            ref={this.contactFormRef}
                            show={showContactsForm}
                            readOnly={contactFormIsReadOnly}
                            allowContactTypeEdit={allowContactTypeEdit}
                            contact={selectedContact}
                            onClose={() => {
                                this.setState({ showContactsForm: false, contactFormIsReadOnly: false });
                            }}
                            onChange={(e) => {
                                const { selectedContact } = { ...this.state };
                                selectedContact[e.target.name] = e.target.value;
                                this.setState({
                                    newContact: { ...selectedContact },
                                });
                            }}
                            onCheckedChanged={(e) => {
                                const { selectedContact } = { ...this.state };
                                selectedContact[e.target.name] = e.target.checked;
                                this.setState({
                                    newContact: { ...selectedContact },
                                });
                            }}
                            onContactTypeChanged={(items) => {
                                const { selectedContact } = this.state;
                                selectedContact.contactTypes = items.map(
                                    (x) => x.value,
                                );
                                this.setState({
                                    newContact: { ...selectedContact },
                                });
                            }}
                            onSaveCallback={this.onOrganizationContactSaved}
                        />
                    </>
                )}

                <ModalForm
                    isOpen={showAddSetupCharge}
                    onSubmit={() => this.onAddSetupCharge()}
                    toggle={() => this.setState({ showAddSetupCharge: false })}
                    title="Add Setup Charge"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Charge Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedSetupChargeTypes?.length ? (
                                <tr>
                                    <th className="invoiceHourlyCharge">
                                        Charge Name
                                    </th>
                                </tr>
                            ) : (

                                unusedSetupChargeTypes?.map(
                                    (
                                        charge,
                                    ) => {
                                        const id = `${charge.id}|${uuid()}`;

                                        return (
                                            <tr
                                                key={id}
                                            >
                                                <td className="text-center">

                                                    <input
                                                        id={id}
                                                        name={id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;

                                                            this.setState({
                                                                charge,
                                                            });

                                                            this.hydrateSetupCharges();
                                                        }}
                                                    />

                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    <label htmlFor={id}>
                                                        {
                                                            charge.resourceName
                                                        }
                                                    </label>
                                                </td>
                                            </tr>
                                        );
                                    },
                                )
                            )}
                        </tbody>
                    </table>
                    <div className="d-flex align-items-center justify-content-between mt-2">
                        <small className="text-muted">
                            {hasSetupCharges ? `${selectedCount} of ${(unusedSetupChargeTypes ?? []).length} selected` : 'No setup charges available'}
                        </small>

                        <Button
                            size="sm"
                            color="primary"
                            outline
                            disabled={!hasSetupCharges}
                            onClick={() => this.toggleAllSetupCharges(!allSetupChargesSelected)}
                        >
                            {selectAllCta}
                        </Button>
                    </div>
                </ModalForm>

                <ModalForm
                    isOpen={showAddCertification}
                    onSubmit={() => this.onAddCertification()}
                    toggle={() => this.setState({ showAddCertification: false })}
                    title="Add Certification Requirement"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Certification Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedCertifications?.length ? (
                                <tr>
                                    <td
                                        className="text-center"
                                    >
                                        No Certification Available
                                    </td>
                                </tr>
                            ) : (

                                unusedCertifications?.map(
                                    (
                                        charge,
                                    ) => {
                                        const id = `${charge.id}|${uuid()}`;

                                        return (
                                            <tr
                                                key={id}
                                            >
                                                <td className="text-center">

                                                    <input
                                                        id={id}
                                                        name={id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;

                                                            this.setState({
                                                                charge,
                                                            });
                                                        }}
                                                    />

                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    <label htmlFor={id}>
                                                        {
                                                            charge.resourceName
                                                        }
                                                    </label>
                                                </td>
                                            </tr>
                                        );
                                    },
                                )
                            )}
                        </tbody>
                    </table>
                </ModalForm>

                {Boolean(currentJobOverride) && (
                    <JobOverrideModal
                        edit={edit}
                        open={showJobOverrideModal}
                        jobOverride={currentJobOverride}
                        setFormOpened={this.context.setFormOpened}
                        onClose={this.handleCloseJobOverride}
                        isReadOnly={this.overrideDayIsDisabled(currentJobOverride.overrideDate)}
                        setOverride={this.setPageModified}
                        jobTypesOptions={jobTypesOptions}
                        timeZone={selectedDispatchLocation?.timeZone}
                        companyContactsOptions={companyContactsOptions}
                        onUpdateContacts={this.onUpdateContacts}
                    />
                )}

                <ModalForm
                    isOpen={showAddPermitFees}
                    onSubmit={() => this.onAddPermitFees()}
                    toggle={() => this.setState({ showAddPermitFees: false })}
                    title="Add Permit Fees"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Charge Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedSetupChargeTypes?.length ? (
                                <tr>
                                    <td
                                        className="text-center"
                                    >
                                        No Permit Fees Available
                                    </td>
                                </tr>
                            ) : (

                                unusedPermitFees?.map(
                                    (
                                        charge,
                                    ) => (
                                        <tr
                                            key={`${charge.id
                                                }|${uuid()}`}
                                        >
                                            <td className="text-center">

                                                <input
                                                    type="checkbox"
                                                    checked={charge.isNew == true}
                                                    onChange={(x) => {
                                                        charge.isNew = x.currentTarget.checked;

                                                        this.setState({
                                                            charge,
                                                        });

                                                        this.hydrateSetupCharges();
                                                    }}
                                                />

                                            </td>
                                            <td className="invoiceHourlyResource">
                                                {
                                                    charge.resourceName
                                                }
                                            </td>

                                        </tr>
                                    ),
                                )
                            )}
                        </tbody>
                    </table>
                </ModalForm>

            </>
        );
    }
}
export default JobForm;
